import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useHistory, useParams } from 'react-router';
import { AxiosError } from 'axios';
import { useSnackbar } from 'notistack';

import { Box, IconButton } from '@mui/material';
import {
  RemoveRedEye as RemoveRedEyeIcon,
  ArrowForwardIos as ArrowForwardIosIcon,
} from '@mui/icons-material';

import handleApiResponse from '../../utils/handleApiResponse';
import { getInstitutionSessionUserTests } from '../../requests/api/institutionTests';

import { ReduxState } from '../../types';
import {
  INSTITUTION_SESSION_TESTS_UI,
  InstitutionSessionUserTestResume,
  InstitutionSessionUserTestRow,
  STATUS_LABELS,
} from './types';

import CircularProgressComponent from '../../components/Loading/CircularProgressComponent';
import Page from '../../components/Layout/Page';
import CustomTable from '../../components/General/CustomTable';
import EmptyTable from '../../components/General/EmptyTable';
import InstitutionUserDetailHeader from './InstitutionUserDetailHeader';

import useStyles from './styles';

function InstitutionUserProfile() {
  const { institutionUrlExtension } = useParams<{ institutionUrlExtension: string }>();
  const [showLoading, setShowLoading] = useState(true);
  const [tests, setTests] = useState<{
    items: InstitutionSessionUserTestRow[],
    totalItems: number,
  }>({
    items: [],
    totalItems: 0,
  });

  const { enqueueSnackbar } = useSnackbar();
  const history = useHistory();

  const account = useSelector((state: ReduxState) => state.institutionAccount);
  const classes = useStyles();

  const testUrl = (test: InstitutionSessionUserTestRow) => {
    const base = `/institucion/${institutionUrlExtension}/ensayos/${test.institutionSessionUserTestId}`;
    return test.canAnswer ? base : `${base}/resultado`;
  };

  useEffect(() => {
    const loadInstitutionSessionUserTests = async () => {
      if (account.institutionUser) {
        try {
          const { institutionSessionUserTests } = await getInstitutionSessionUserTests(
            institutionUrlExtension, account.institutionUser.id,
          );
          const updatedTests = institutionSessionUserTests.map(
            (test: InstitutionSessionUserTestResume) => {
              const canAnswer = test.status === 'En proceso' && test.active === true;
              const row: InstitutionSessionUserTestRow = {
                ...test,
                status: STATUS_LABELS[test.status] ?? test.status,
                canAnswer,
              };
              const url = testUrl(row);
              row.action = (
                <IconButton
                  onClick={(e) => {
                    e.stopPropagation();
                    history.push(url);
                  }}
                >
                  {canAnswer
                    ? <ArrowForwardIosIcon className={classes.icons} />
                    : <RemoveRedEyeIcon className={classes.icons} />}
                </IconButton>
              );
              return row;
            },
          );

          setTests({
            items: updatedTests,
            totalItems: institutionSessionUserTests.length,
          });
        } catch (err) {
          const e = err as AxiosError;
          handleApiResponse(enqueueSnackbar, e, false);
        }
      }
      setShowLoading(false);
    };
    loadInstitutionSessionUserTests();
  }, [institutionUrlExtension]);

  return (
    <Page title="Alumnos">
      {showLoading
        ? <CircularProgressComponent />
        : <>
          {account.institutionUser && (
            <Box mt={3}>
              <InstitutionUserDetailHeader student={account.institutionUser} />
              {tests.totalItems !== 0
                && <CustomTable
                  headers={INSTITUTION_SESSION_TESTS_UI}
                  data={{ values: tests.items, count: tests.totalItems }}
                  onRowClick={(row) => history.push(testUrl(row as InstitutionSessionUserTestRow))}
                />}
              {tests.totalItems === 0 && <EmptyTable />}
            </Box>
          )}
        </>}
    </Page>
  );
}

export default InstitutionUserProfile;
